import { logger } from '@/lib/logger';

const STORAGE_KEY_PREFIX = 'ahoy-dashboard-section-prefs-';

export type MeetingsView = 'compact' | 'dayview';
export type PipelineView = 'bucketed' | 'stagewise';

export interface DashboardSectionPrefs {
  meetings: MeetingsView;
  pipeline: PipelineView;
}

export const DEFAULT_PREFS: DashboardSectionPrefs = {
  meetings: 'compact',
  pipeline: 'bucketed',
};

const VALID_MEETINGS_VIEWS: ReadonlySet<string> = new Set<MeetingsView>([
  'compact',
  'dayview',
]);
const VALID_PIPELINE_VIEWS: ReadonlySet<string> = new Set<PipelineView>([
  'bucketed',
  'stagewise',
]);

function getStorageKey(userId: number, orgId: number): string {
  return `${STORAGE_KEY_PREFIX}${userId}-${orgId}`;
}

export function getDashboardSectionPrefs(
  userId: number | undefined,
  orgId: number | undefined
): DashboardSectionPrefs {
  if (userId === undefined || orgId === undefined) {
    return { ...DEFAULT_PREFS };
  }

  try {
    const raw = localStorage.getItem(getStorageKey(userId, orgId));
    if (!raw) {
      return { ...DEFAULT_PREFS };
    }
    const parsed = JSON.parse(raw) as Partial<DashboardSectionPrefs>;
    return {
      meetings:
        typeof parsed.meetings === 'string' &&
        VALID_MEETINGS_VIEWS.has(parsed.meetings)
          ? parsed.meetings
          : DEFAULT_PREFS.meetings,
      pipeline:
        typeof parsed.pipeline === 'string' &&
        VALID_PIPELINE_VIEWS.has(parsed.pipeline)
          ? parsed.pipeline
          : DEFAULT_PREFS.pipeline,
    };
  } catch (err) {
    logger.warn('Failed to read dashboard section prefs from localStorage', {
      component: 'dashboard-section-prefs',
      error: String(err),
    });
    return { ...DEFAULT_PREFS };
  }
}

export function setDashboardSectionPrefs(
  userId: number,
  orgId: number,
  prefs: DashboardSectionPrefs
): void {
  try {
    localStorage.setItem(getStorageKey(userId, orgId), JSON.stringify(prefs));
  } catch (err) {
    logger.warn('Failed to persist dashboard section prefs to localStorage', {
      component: 'dashboard-section-prefs',
      error: String(err),
    });
  }
}

export function updateDashboardSectionPref<
  K extends keyof DashboardSectionPrefs,
>(
  userId: number,
  orgId: number,
  key: K,
  value: DashboardSectionPrefs[K]
): void {
  const current = getDashboardSectionPrefs(userId, orgId);
  setDashboardSectionPrefs(userId, orgId, { ...current, [key]: value });
}

export function clearDashboardSectionPrefs(
  userId: number,
  orgId: number
): void {
  try {
    localStorage.removeItem(getStorageKey(userId, orgId));
  } catch (err) {
    logger.warn('Failed to clear dashboard section prefs from localStorage', {
      component: 'dashboard-section-prefs',
      error: String(err),
    });
  }
}

export function clearAllDashboardSectionPrefs(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (err) {
    logger.warn('Failed to clear dashboard section prefs from localStorage', {
      component: 'dashboard-section-prefs',
      error: String(err),
    });
  }
}
